import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CheckboxProps, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FrequencyDates, FrequencyDatesToBack, NewsTabs, WeekDays } from 'shared/enums/news';
import { useGetNewsByIdQuery, useUpdateNewsMutation, useUpdateStatusMutation } from 'app/services/edpApiV1/news';
import {
  addNotifications,
  dataForAddUserTable,
  prepareDateTimeToFill,
  prepareDateTimeToSave,
} from 'shared/helpers/utils';
import { TAddUserEmployee } from 'shared/types/news.types';
import { TDivisionResponse, TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import { PICKER_TIME_MASK } from 'shared/constants';
import dayjs from 'dayjs';
import { MINUTE_STEP } from 'shared/constants/date';
import { getClosestBiggerTime } from 'shared/helpers/date';
import NewsEditor from '../../../shared/components/text-editor/TextEditor';
import PickUsers from '../pickUsers/PickUsers';
import Frequency from '../Frequency/Frequency';
import DateTime from '../DateTime/DateTime';
import ModalCancelCreating from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import Title from '../../../shared/components/title/Title';
import style from './index.module.scss';
import TopBlock from './topBlock/TopBlock';

const UpdateNews = () => {
  const { id } = useParams();
  const { data } = useGetNewsByIdQuery({ id: Number(id) });
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const [updateNewsQuery, { isLoading: isUpdateLoading }] = useUpdateNewsMutation();
  const [updateNewsStatusQuery, { isLoading: isUpdateStatusLoading }] = useUpdateStatusMutation();

  const navigate = useNavigate();

  const [title, setTitle] = useState<string>('');
  const [created, setCreated] = useState<string>('');
  const [retry, setRetry] = useState<FrequencyDates>(FrequencyDates.NONE);
  const [text, setText] = useState<string>('');
  const [users, setUsers] = useState<TAddUserEmployee[]>([]);
  const [divisions, setDivisions] = useState<TDivisionResponse[]>([]);
  const [organizations, setOrganizations] = useState<TOrganizationResponse[]>([]);
  const [datesToCreate, setDatesToCreate] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>();
  const [time, setTime] = useState<string | undefined | null>(getClosestBiggerTime());
  const [days, setDays] = useState<WeekDays[]>([]);
  const [status, setStatus] = useState<NewsTabs>(NewsTabs.UNPUBLICATED);
  const possibleMinutes = useRef<number[]>([]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setCreated(data.created);
    setTitle(data.title);
    setRetry(FrequencyDates[data.retry.toUpperCase()]);
    setText(data.content);
    setUsers(dataForAddUserTable(data.users));
    setDivisions(data.divisions);
    setOrganizations(data.organizations);
    setStatus(data.status);

    const { normalDate, normalTime, normalDatesToCreate, normalDays } = prepareDateTimeToFill(data);

    setDatesToCreate(normalDatesToCreate);
    setStartDate(normalDate);
    setTime(normalTime);
    setDays(normalDays);
  }, [data]);

  useEffect(() => {
    const date = dayjs(time, PICKER_TIME_MASK);
    const minute = date.minute();

    if (!possibleMinutes.current.includes(minute) && time) {
      const candidate = { diff: Infinity, value: 0 };

      possibleMinutes.current.forEach(elem => {
        const newDiff = Math.abs(minute - elem);
        if (newDiff < candidate.diff) {
          candidate.diff = newDiff;
          candidate.value = elem;
        }
      });
      const newDate = date.set('minute', candidate.value);
      const formattedDateString = newDate.format(PICKER_TIME_MASK);
      setTime(formattedDateString);
      return;
    }
  }, [time]);

  useEffect(() => {
    let currentMinute = 0;
    while (currentMinute < 60) {
      possibleMinutes.current.push(currentMinute);
      currentMinute += MINUTE_STEP;
    }
  }, []);

  const onTitleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(event.target.value);
  };

  const onDefferedPublicationToggled: CheckboxProps['onChange'] = e => {
    if (e.target.checked === false) {
      setRetry(FrequencyDates.NONE);
    } else {
      setRetry(FrequencyDates.ONCE);
    }
  };

  const prepareNewsToSave = useCallback(() => {
    const { utcDate, utcTime, utcDatesToCreate, utcDays } = prepareDateTimeToSave({
      datesToCreate,
      days,
      retry,
      startDate,
      time,
    });
    const isNotDeferred = retry !== FrequencyDates.NONE;

    return {
      id: Number(id),
      created: created,
      title: title,
      content: text,
      userIds: users ? users.map(user => user.id) : [],
      divisionIds: divisions.map(user => user.id),
      organizationIds: organizations.map(user => user.id),
      retry: FrequencyDatesToBack[retry],
      datesToCreate: isNotDeferred ? utcDatesToCreate : [],
      startDate: isNotDeferred ? utcDate : undefined,
      time: isNotDeferred ? utcTime : undefined,
      days: isNotDeferred ? utcDays : undefined,
    };
  }, [created, datesToCreate, days, divisions, id, organizations, retry, startDate, text, time, title, users]);

  const validateNews = (): boolean => {
    return !title || !text;
  };

  const openCancelUpdateModalSubmit = () => {
    setOpenCanelModal(false);
    navigate('/news');
  };

  const navigateAfterSuccess = () => {
    navigate('/news');
  };

  const saveNews = async () => {
    await addNotifications(
      updateNewsQuery(prepareNewsToSave()),
      'Новость изменена',
      'Ошибка при изменении новости',
      navigateAfterSuccess,
    );
  };

  const publicate = async () => {
    await addNotifications(
      updateNewsStatusQuery({ id: Number(id), status: NewsTabs.PUBLICATED }),
      'Новость опубликована',
      'Ошибка при публикации новости',
      navigateAfterSuccess,
    );
  };

  const archiveNews = async () => {
    await addNotifications(
      updateNewsStatusQuery({ id: Number(id), status: NewsTabs.ARCHIVE }),
      'Новость занесена в архив',
      'Ошибка при архивировании новости',
      navigateAfterSuccess,
    );
  };

  const onEditorValueChange = (val: string) => {
    setText(val);
  };

  const changeUsers = (usersToChange: TAddUserEmployee[]) => {
    setUsers(usersToChange);
  };

  const changeDivisions = (divisionsToChange: TDivisionResponse[]) => {
    setDivisions(divisionsToChange);
  };

  const changeOrganizations = (organizationsToChange: TOrganizationResponse[]) => {
    setOrganizations(organizationsToChange);
  };

  const onFrequencyChange = (data: FrequencyDates) => {
    setRetry(data);
  };

  const onDateChange = (dates: string[] | string, isStartDate: boolean) => {
    return isStartDate ? setStartDate(dates as string) : setDatesToCreate(dates as string[]);
  };

  const onTimeChange = (time: string | undefined | null) => {
    setTime(time);
  };

  const onDaysChange = (days: WeekDays[]) => {
    setDays(days);
  };

  return (
    <Spin spinning={!title}>
      <div className={style.wrap}>
        <TopBlock
          id={id}
          archiveNews={archiveNews}
          publicate={publicate}
          navigate={navigate}
          isUpdateStatusLoading={isUpdateStatusLoading}
        />
        <Title onTitleChange={onTitleChange} title={title} />
        <NewsEditor content={text} onValueChange={onEditorValueChange} title="Текст новости" />
        <PickUsers
          changeUsers={changeUsers}
          selectedUsers={users}
          selectedDivisions={divisions}
          addDivisions={changeDivisions}
          addOrganizations={changeOrganizations}
          selectedOrganizations={organizations}
        />
        <div className={style['deferred-block']}>
          <div className={style.deferred}>
            <Checkbox checked={retry !== FrequencyDates.NONE} onChange={onDefferedPublicationToggled}>
              Отложенная публикация
            </Checkbox>
          </div>
          {retry !== FrequencyDates.NONE ? (
            <div className={style.date}>
              <Frequency retry={retry} onChange={onFrequencyChange} />
              <DateTime
                retry={retry}
                datesToCreate={datesToCreate}
                startDate={startDate}
                days={days}
                time={time as string}
                onDateChange={onDateChange}
                onTimeChange={onTimeChange}
                onDaysChange={onDaysChange}
              />
            </div>
          ) : null}
        </div>
        <div className={style['update-btns']}>
          <Button type="primary" onClick={saveNews} disabled={validateNews()} loading={isUpdateLoading}>
            Сохранить
          </Button>
          <ModalCancelCreating
            onStay={() => {
              setOpenCanelModal(false);
            }}
            open={openCancelModal}
            onQuit={openCancelUpdateModalSubmit}
          />
        </div>
      </div>
    </Spin>
  );
};

export default UpdateNews;
